import { useRef, useState } from "react";

import { getErrorMessage, isAPIError } from "@/core/services";
import { useAsyncList, useToast } from "@/ds";
import {
  aplicarReferenciaNaObra,
  listarObrasDaReferencia,
} from "@/modules/financeiro-configuracoes";
import type { ObraDaReferencia } from "@/modules/financeiro-configuracoes";
import { HUB_PERMISSOES, usePermissoes } from "@/modules/permissoes";
import { novaChave } from "@/shared/hooks";
import {
  APLICAR_ERRO_LISTA,
  APLICAR_ERRO_OBRA,
  APLICAR_SEM_PERMISSAO,
  APLICAR_TOAST_TITULO,
  FECHAR_LABEL,
  OK_LABEL,
  aplicadaToast,
  escoposAfetados,
} from "./aplicar-referencia-obras-dialog.constants";
import type {
  AplicarReferenciaObrasDialogProps,
  AplicarReferenciaObrasDialogViewModel,
  EstadoListaObras,
  SituacaoLinhaObra,
} from "./types-aplicar-referencia-obras-dialog";

interface ParamsLista {
  tipo: AplicarReferenciaObrasDialogProps["tipo"];
  referenciaId: string;
}

export function useAplicarReferenciaObrasDialog({
  tipo,
  referenciaId,
  onClose,
}: AplicarReferenciaObrasDialogProps): AplicarReferenciaObrasDialogViewModel {
  const { hasPermissao } = usePermissoes();
  const toast = useToast();
  const oferecido = hasPermissao(HUB_PERMISSOES.FINANCEIRO_EDIT_CONFIGURACAO);
  const [situacoes, setSituacoes] = useState<Record<string, SituacaoLinhaObra>>({});
  const [erros, setErros] = useState<Record<string, string>>({});
  const chaves = useRef<Record<string, string>>({});

  const lista = useAsyncList<ParamsLista, ObraDaReferencia>({
    fetcher: async (params, signal) => {
      const obras = await listarObrasDaReferencia(params.tipo, params.referenciaId, { signal });
      return { items: obras, total: obras.length };
    },
    params: { tipo, referenciaId },
    enabled: oferecido,
    forbiddenMessage: APLICAR_SEM_PERMISSAO,
    errorMessage: APLICAR_ERRO_LISTA,
    resolveErrorMessage: getErrorMessage,
  });

  const situacaoDe = (obraId: string): SituacaoLinhaObra => situacoes[obraId] ?? "pendente";
  const definirSituacao = (obraId: string, situacao: SituacaoLinhaObra) =>
    setSituacoes((atual) => ({ ...atual, [obraId]: situacao }));

  const aplicar = async (obra: ObraDaReferencia) => {
    if (situacaoDe(obra.obraId) !== "pendente") return;
    definirSituacao(obra.obraId, "aplicando");
    setErros(({ [obra.obraId]: _, ...demais }) => demais);
    const chave = (chaves.current[obra.obraId] ??= novaChave());
    try {
      await aplicarReferenciaNaObra(tipo, referenciaId, obra.obraId, { idempotencyKey: chave });
      delete chaves.current[obra.obraId];
      definirSituacao(obra.obraId, "aplicada");
      toast.success(APLICAR_TOAST_TITULO, aplicadaToast(obra.codigo));
    } catch (falha) {
      if (isAPIError(falha) && falha.status >= 400 && falha.status < 500 && falha.status !== 408) {
        delete chaves.current[obra.obraId];
      }
      setErros((atual) => ({
        ...atual,
        [obra.obraId]: getErrorMessage(falha) || APLICAR_ERRO_OBRA,
      }));
      definirSituacao(obra.obraId, "pendente");
    }
  };

  let estado: EstadoListaObras = "lista";
  if (lista.status === "error" || lista.status === "forbidden") estado = "erro";
  else if (lista.isLoading || lista.status === "idle") estado = "carregando";
  else if (lista.items.length === 0) estado = "vazio";

  return {
    oferecido,
    estado,
    erro: lista.errorMessage,
    linhas: lista.items.map((obra) => ({
      obraId: obra.obraId,
      rotulo: `${obra.codigo} · ${obra.nome}`,
      testIds: {
        linha: `aplicar-referencia-obra-${obra.obraId}`,
        erro: `aplicar-referencia-erro-${obra.obraId}`,
        aplicada: `aplicar-referencia-aplicada-${obra.obraId}`,
        aplicar: `aplicar-referencia-aplicar-${obra.obraId}`,
      },
      escopos: escoposAfetados(obra.escopos.map((escopo) => escopo.nome)),
      situacao: situacaoDe(obra.obraId),
      erro: erros[obra.obraId] ?? null,
      aplicar: () => void aplicar(obra),
    })),
    recarregar: lista.reload,
    fecharLabel: estado === "vazio" ? OK_LABEL : FECHAR_LABEL,
    fechar: onClose,
  };
}
